import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { Subject, takeUntil } from 'rxjs';
import { IdleWarningDialogComponent } from '../../../idle-warning-dialog/idle-warning-dialog.component';
import { AuthService } from '../auth/auth.service';

@Injectable({
  providedIn: 'root',
})
export class IdleTimeoutService {
  private idle = inject(Idle);
  private keepalive = inject(Keepalive);
  private authService = inject(AuthService);
  private dialog = inject(MatDialog);
  private broadcastChannel = new BroadcastChannel('portal_idle_broadcast');

  private isUserIdle = false;
  private tabId: any | null = null;
  private heartbeatInterval: number | undefined;
  private tokenCheckInterval: any;
  private logoutCountdownSubject = new Subject<number>();
  private destroy$ = new Subject<void>();

  private readonly TAB_LEADER_KEY = 'testhub_leader';
  private readonly TAB_LEADER_HEARTBEAT_KEY = 'testhub_leader_heartbeat';
  private readonly LEADER_TIMEOUT = 5000; // leader times out after 5 seconds of no heartbeat
  private readonly TIMEOUT_INTERVAL = 5 * 60; // log out after 5 minutes of inactivity after warning
  private readonly IDLE_WARNING_INTERVAL = 15 * 60; // show idle modal after 15 minutes of inactivity

  // Broadcast message keys
  private readonly LOGOUT = 'logout';
  private readonly IDLE_RESET = 'idle_reset';
  private readonly LEADER_REMOVED = 'leader_removed';

  logoutCountdown$ = this.logoutCountdownSubject.asObservable();

  constructor() {
    this.broadcastChannel.onmessage = (event: MessageEvent) => {
      switch (event.data) {
        case this.LOGOUT:
          this.authService.clearCache();
          break;
        case this.IDLE_RESET:
          this.onResetBroadcast();
          break;
        case this.LEADER_REMOVED:
          this.tryBecomeLeader();
          break;
        default:
          break;
      }
    };
  }

  start() {
    this.tryBecomeLeader();
    this.monitorToken();
    this.initializeIdle();
    window.addEventListener('beforeunload', () => this.destroy());
  }

  initiateReset(): void {
    this.broadcastChannel.postMessage(this.IDLE_RESET);
    this.reset();
    this.handleTokenRefresh();
  }

  private onResetBroadcast(): void {
    this.closeIdleWarningDialog();
    this.reset();
  }

  private reset(): void {
    this.isUserIdle = false;
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
    this.idle.watch();
  }

  private initiateLogout(): void {
    if (this.tryBecomeLeader()) {
      this.broadcastChannel.postMessage(this.LOGOUT);
      this.authService.logout();
    }
  }

  private initializeIdle(): void {
    this.idle.setIdle(this.IDLE_WARNING_INTERVAL);
    this.idle.setTimeout(this.TIMEOUT_INTERVAL);
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
    this.keepalive.interval(20); // ping every 20 seconds

    this.idle.onIdleStart.subscribe(() => {
      if (!this.isUserIdle) {
        this.authService.refreshToken();
        this.idle.clearInterrupts();
        this.openIdleWarningDialog();
        this.isUserIdle = true;
      }
    });

    // this.idle.onInterrupt.subscribe(() => console.log(new Date()));
    this.idle.onTimeoutWarning.subscribe((countdown) =>
      this.logoutCountdownSubject.next(countdown),
    );
    this.idle.onTimeout.subscribe(() => this.initiateLogout());

    this.idle.watch();
  }

  private openIdleWarningDialog(): void {
    const dialogRef = this.dialog.open(IdleWarningDialogComponent, {
      id: 'idle-warning-dialog',
      disableClose: true,
      autoFocus: false,
      position: { top: '150px' },
      width: '350px',
      minHeight: '200px',
      panelClass: 'dialog',
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result) {
          this.initiateReset();
        }
      });
  }

  private closeIdleWarningDialog(): void {
    this.dialog.getDialogById('idle-warning-dialog')?.close();
  }

  private tryBecomeLeader(): boolean {
    if (!this.tabId) {
      this.tabId = crypto.randomUUID();
    }
    let currentLeader = localStorage.getItem(this.TAB_LEADER_KEY);
    const lastHeartbeat = Number(localStorage.getItem(this.TAB_LEADER_HEARTBEAT_KEY));
    const now = Date.now();

    if (!currentLeader || !lastHeartbeat || now - lastHeartbeat > this.LEADER_TIMEOUT) {
      currentLeader = this.tabId;
      localStorage.setItem(this.TAB_LEADER_KEY, this.tabId);
      localStorage.setItem(this.TAB_LEADER_HEARTBEAT_KEY, now.toString());
      this.startHeartbeat();
    }

    return this.tabId.toString() === currentLeader;
  }

  private destroy(): void {
    if (localStorage.getItem(this.TAB_LEADER_KEY) === this.tabId) {
      localStorage.removeItem(this.TAB_LEADER_KEY);
      localStorage.removeItem(this.TAB_LEADER_HEARTBEAT_KEY);
      this.broadcastChannel.postMessage(this.LEADER_REMOVED);
    }
    this.destroy$.next();
    this.destroy$.complete();
    window.clearInterval(this.heartbeatInterval);
    window.clearInterval(this.tokenCheckInterval);
  }

  private startHeartbeat(): void {
    this.heartbeatInterval = window.setInterval(() => {
      localStorage.setItem(this.TAB_LEADER_HEARTBEAT_KEY, Date.now().toString());
    }, 2000);
  }

  private handleTokenRefresh(): void {
    const minutesToTokenExpiration =
      Math.floor(this.authService.tokenExpirationSig()!.getTime() - new Date().getTime()) /
      (1000 * 60);
    if (!this.isUserIdle && minutesToTokenExpiration <= 10) {
      this.authService.refreshToken();
    }
  }

  private monitorToken(): void {
    this.tokenCheckInterval = setInterval(() => this.handleTokenRefresh(), 5 * 60000); // check every 5 minutes
  }
}
